/**
 * Normalizes URLs by removing invisible Unicode characters and cleaning the pathname
 * This is particularly useful for handling Facebook and other social media tracking parameters
 * that may add invisible characters to the URL path.
 */

/**
 * Removes invisible Unicode characters from a string
 * @param {string} str - The string to clean
 * @returns {string} - The cleaned string
 */
export const removeInvisibleChars = (str) => {
    if (!str) return str;

    // Remove common invisible Unicode characters that social media platforms add
    // Including: zero-width non-joiner, zero-width joiner, zero-width space, etc.
    return str.replace(/[\u200B-\u200D\uFEFF\u200C\u200D\u2060\u00AD]/g, '');
};

/**
 * Normalizes a URL pathname by:
 * - Removing invisible Unicode characters
 * - Removing trailing slashes
 * - Handling encoded characters
 * - Removing query parameters for routing purposes
 * @param {string} pathname - The pathname to normalize
 * @returns {string} - The normalized pathname
 */
export const normalizePathname = (pathname) => {
    if (!pathname) return '/';

    // Decode URI component first to handle encoded characters like %E2%80%AC
    try {
        // Decode multiple times in case of double encoding
        let decoded = pathname;
        let prevDecoded = '';
        while (decoded !== prevDecoded) {
            prevDecoded = decoded;
            try {
                decoded = decodeURIComponent(decoded);
            } catch (e) {
                break;
            }
        }
        pathname = decoded;
    } catch (e) {
        // If decoding fails, use original
    }

    // Remove invisible characters (zero-width spaces, non-joiners, etc.)
    pathname = removeInvisibleChars(pathname);

    // Remove trailing slashes (except for root)
    pathname = pathname.replace(/\/+$/, '') || '/';

    // Remove leading slashes (we'll add one back)
    pathname = pathname.replace(/^\/+/, '');

    return '/' + pathname;
};

/**
 * Maps common URL variations to their canonical routes
 * This helps handle typos and variations in social media links
 */
const routeVariations = {
    '/download': '/downloadapp',
    '/download-app': '/downloadapp',
    '/download-apps': '/downloadapp',
    '/downloadpage': '/downloadapp',
    '/dowloadapp': '/downloadapp', // Common typo
    '/downloadap': '/downloadapp', // Common typo
    '/usd-bank-account': '/accounts',
    '/eur-bank-account': '/accounts',
};

/**
 * Gets the canonical route for a given pathname
 * @param {string} pathname - The pathname to normalize
 * @returns {string} - The canonical route
 */
export const getCanonicalRoute = (pathname) => {
    const normalized = normalizePathname(pathname);

    // Check if there's a variation mapping
    if (routeVariations[normalized]) {
        return routeVariations[normalized];
    }

    return normalized;
};

/**
 * Checks if a pathname matches a route, accounting for variations
 * @param {string} pathname - The pathname to check
 * @param {string} route - The route to match against
 * @returns {boolean} - Whether the pathname matches the route
 */
export const matchesRoute = (pathname, route) => {
    const normalized = normalizePathname(pathname);
    const canonical = getCanonicalRoute(normalized);
    const routeNormalized = normalizePathname(route);

    return canonical === routeNormalized || normalized === routeNormalized;
};

